import { modelData } from 'app/lib/modelData'
import { observer } from 'mobx-react-lite'
import type { CreateTaskInput } from 'app/generated/gql/graphql'
/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Calendar, Group, MapPin, NavArrowDown, ProfileCircle, User, Xmark } from 'iconoir-react'
import { assigneeInput, ROLE_ASSIGNEE_PREFIX } from 'lib/taskAssignee'
import { RoleOptionLabel } from 'app/components/tasks/TaskAssigneeLabel'
import moment from 'moment-timezone'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import CalendarPicker from '../marketing/CalendarPicker'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command'
import { Input } from '../ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Textarea } from '../ui/textarea'
import { Button } from '../ui/button'
import NameIcon from '../shared/NameIcon'
import { MODIFIER_KEY } from 'lib/modifierKey'
import type { Contact } from 'app/generated/graphql'
import type { ContactJson, TaskJson } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { assigneesFetcher } from 'app/lib/fetchers/assigneesFetcher'
import { useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { logWarn } from 'lib/logging'
import classNames from 'classnames'
import { cn } from 'app/lib/utils'
import { useContactStore, useLocations, useTaskStore } from 'app/stores/StoreContext'
import { useResolveTaskOwner } from './useResolveTaskOwner'
import { TaskTemplatePicker } from 'app/components/task-templates/TaskTemplatePicker'
import { applyTaskTemplate } from 'app/lib/taskTemplates'
import { substituteTokens } from 'app/lib/messageTemplates'
import type { TaskTemplate } from 'app/generated/graphql'
import type { TaskValue } from 'app/components/table/bulkActions/types'

type ContactOpt = { id: string; name?: string; locationId?: string }
type UserOpt = { id: string; name?: string; email?: string; kind?: `user` | `role`; disabled?: boolean }
type LocationOpt = { id: string; name?: string }

// Schema for the quick-create form. Validation runs through zod via
// `@hookform/resolvers/zod`, so the submit handler only fires with values
// that have already been checked against this shape. The non-native fields
// (dueDate / owner / contact) are managed via `Controller` so they get the
// same validation pass as the native title/description inputs.
const quickCreateSchema = z.object({
  title: z.string().trim().min(1, { message: `Title is required` }),
  description: z.string().optional(),
  dueDate: z.date(),
  owner: z
    .object({
      id: z.string(),
      name: z.string().optional(),
      email: z.string().optional(),
    })
    .optional(),
  contact: z
    .object({
      id: z.string(),
      name: z.string().optional(),
      locationId: z.string().optional(),
    })
    .optional(),
  location: z.object({ id: z.string(), name: z.string().optional() }).optional(),
})
// A task without a contact still needs a location; with one, the location follows the contact.
const quickCreateResolverSchema = quickCreateSchema.refine(
  (values) => !!(values.contact?.locationId || values.location?.id),
  { message: `Pick a contact or a location`, path: [`location`] },
)

type QuickCreateValues = z.infer<typeof quickCreateSchema>

/**
 * Lean, keyboard-first task creator. Every create flow uses it; editing an
 * existing task uses the full EditTaskModal form.
 *
 * Design goals:
 *   - Title is the only required input you see at rest. Most tasks ARE just
 *     a title + a date; we shouldn't make users tab past a 200px WYSIWYG.
 *   - Due / Owner / Contact render as chips with sensible defaults
 *     (today / current user / none) — click to change.
 *   - Description starts hidden; click "+ Add description" to reveal.
 *   - ⌘↵ saves from anywhere in the form (Enter while in title also saves).
 *   - Esc cancels (handled by the Modal wrapper).
 *
 * Bulk mode (`onBulkSubmit` set): the contact chip shows the selection count and
 * the form hands its raw values to the bulk apply instead of creating anything.
 * Tokens stay unresolved and the location comes from each contact, per row.
 */
// observer: reads taskStore.saving in render for the Save button state. Task is a
// store-owned entity, so create goes through TaskStore (ADR-0001): the store runs the
// mutation AND syncs its observable list/current in runInAction.
export const QuickCreateTaskModal = observer(function QuickCreateTaskModal({
  onTaskCreatedOrUpdated,
  defaultContact,
  defaultDueDate,
  workspaceId: passedWorkspaceId,
  bulkCount = 0,
  onBulkSubmit,
}: {
  onTaskCreatedOrUpdated: (task?: TaskJson) => void
  defaultContact?: ContactJson
  /** The calendar's clicked day. */
  defaultDueDate?: Date
  workspaceId?: string
  bulkCount?: number
  onBulkSubmit?: (value: TaskValue) => void
}) {
  const me = useUser()

  if (!me) {
    return <></>
  }

  const contextWorkspace = useWorkspace()
  const workspaceId = passedWorkspaceId ?? contextWorkspace?.id
  const isBulk = !!onBulkSubmit

  // ─── Form (react-hook-form + zod) ────────────────────────────────────────
  // Default due date = today. The fastest path through the modal is "type a
  // title, hit ⌘↵" — that should produce a usable task without touching
  // anything else.
  const form = useForm<QuickCreateValues>({
    resolver: zodResolver(isBulk ? quickCreateSchema : quickCreateResolverSchema) as never,
    defaultValues: {
      title: ``,
      description: ``,
      dueDate: moment(defaultDueDate).startOf(`day`).toDate(),
      owner: { id: me.id, name: me.name ?? `` },
      contact: defaultContact
        ? {
            id: defaultContact.id!,
            name: defaultContact.name ?? undefined,
            locationId: defaultContact.locationId!,
          }
        : undefined,
    },
    mode: `onSubmit`,
  })
  const { register, handleSubmit, control, watch, formState, setFocus, setValue } = form

  // Description starts hidden; click "+ Add description" to reveal. This is
  // pure UI state, not part of the form payload, so it stays as useState.
  const [showDescription, setShowDescription] = useState(false)

  // React Hook Form's watch API is intentionally live and must not be memoized.
  // oxlint-disable-next-line react/incompatible-library
  const watchedContact = watch(`contact`)
  const taskLocationId = watchedContact?.locationId ?? watch(`location.id`)

  // Locations come from LocationStore: the location chip lists the workspace's locations, and
  // the task location's IANA timezone (e.g. "America/Los_Angeles") anchors the due date in the
  // location's day rather than the user's. A NY user creating a task for an LA clinic at 10pm
  // ET would otherwise be marking it for the next day in LA — which is not what they mean by
  // "today". The submit path falls back to the user's zone until the list has loaded.
  const { list: locations, loading: locationsLoading } = useLocations()
  const locationTimezone = locations.find((location) => location.id === taskLocationId)?.timezone || undefined
  const workspaceLocations: LocationOpt[] = workspaceId
    ? locations
        .filter((location) => location.workspaceId === workspaceId)
        .map(({ id, name }) => ({ id, name: name ?? undefined }))
    : []
  const resolveOwner = useResolveTaskOwner()

  // When the description is revealed, focus the textarea so the user
  // doesn't have to click again.
  useEffect(() => {
    if (showDescription) setFocus(`description`)
  }, [showDescription, setFocus])

  // ─── Submit ──────────────────────────────────────────────────────────────
  const taskStore = useTaskStore()
  const isSaving = taskStore.saving || formState.isSubmitting

  const onSubmit = handleSubmit(
    async (values) => {
      if (onBulkSubmit) {
        // Noon local keeps the picked day intact once the apply serializes it to UTC.
        onBulkSubmit({
          title: values.title.trim(),
          dueDate: moment(values.dueDate).hour(12).format(),
          description: values.description?.trim() || undefined,
          ownerId: values.owner?.id,
        })
        return
      }
      const contactId = values.contact?.id
      // The resolver guarantees one of these is set.
      const locationId = (values.contact?.locationId ?? values.location?.id)!
      const trimmedDescription = values.description?.trim() ?? ``

      // Final token-resolution pass against the FINAL contact. A template can
      // be applied before a contact is picked (handleApplyTemplate falls back
      // to whatever's watched at apply-time, or nothing at all), so the title/
      // description may still carry unresolved `{{contact.*}}` tokens here —
      // re-resolve against the contact that's actually being submitted.
      const tokenContact =
        defaultContact && defaultContact.id === contactId
          ? defaultContact
          : contactId
            ? ({ id: contactId, name: values.contact?.name } as ContactJson)
            : undefined
      const resolvedTitle = substituteTokens(
        values.title.trim(),
        { contact: tokenContact },
        { keepUnresolved: true },
      ).text
      const resolvedDescription = trimmedDescription
        ? substituteTokens(trimmedDescription, { contact: tokenContact }, { keepUnresolved: true }).text
        : ``

      // dueDate: anchor at *noon in the LOCATION's timezone*. The picker gives
      // us a Date at the user's local midnight, but the task is operationally
      // meaningful in the location's day, not the user's. So:
      //   1. Pull the y/m/d from the picked Date.
      //   2. Construct that y/m/d at noon in the location's TZ via moment.tz.
      //   3. Serialize as ISO-with-offset.
      // Noon-local is the safe anchor — ±12h still lands on the same calendar
      // day in any timezone, so display/grouping is consistent everywhere.
      // Falls back to the user's local zone when the location's timezone
      // hasn't loaded yet (or the location has none on file).
      const tz = locationTimezone || moment.tz.guess()
      const dueDateIso = moment
        .tz(
          // `date` (not `day`) so moment treats this as day-of-month rather
          // than day-of-week. month is 0-indexed.
          {
            year: values.dueDate.getFullYear(),
            month: values.dueDate.getMonth(),
            date: values.dueDate.getDate(),
            hour: 12,
          },
          tz,
        )
        .format()

      // Build the input incrementally so optional fields are *omitted*
      // entirely when empty, never sent as `undefined`/`null`. urql's variable
      // serialization can sometimes coerce undefined to null, and we've seen
      // the API reject null for several "optional" fields. `workspaceId` is
      // deliberately omitted — the API derives it server-side from the
      // contact/location/auth context. Sending it returns:
      //   `Field "workspaceId" is not defined by type "CreateTaskInput".`
      const input: CreateTaskInput = {
        title: resolvedTitle,
        dueDate: dueDateIso,
        locationId,
      }
      if (contactId) input.contactId = contactId
      Object.assign(input, assigneeInput(values.owner?.id))
      if (resolvedDescription) input.description = resolvedDescription

      const { data, error } = await taskStore.create(input)
      if (error || !data?.createTask) {
        notifyError(`Failed to create task${error?.message ? `: ${error.message}` : ``}`)
        return
      }
      notifySuccess(`Task created`)
      closeModal()
      // TaskStore returns the mutation data with the task fields inline (no
      // fragment mask), so pass the plain object straight to modelData.
      onTaskCreatedOrUpdated(modelData(data.createTask))
    },
    // Surface validation errors via toast so users who hit ⌘↵ without filling
    // required fields get the same feedback as before the rhf migration.
    (errors) => {
      const firstError = errors.title?.message ?? errors.location?.message ?? `Form has errors`
      notifyError(String(firstError))
      if (errors.title) setFocus(`title`)
    },
  )

  // ⌘↵ / Ctrl+↵ saves from anywhere; plain ↵ in the title input also saves
  // (handled by the native form submit since the title input lives inside
  // the <form>). Scoped to the modal root so we don't capture global
  // keystrokes.
  const onRootKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === `Enter`) {
      e.preventDefault()
      bg(onSubmit())
    }
  }

  // Apply a task template: fills title/description/due/owner from whatever
  // contact context we have right now (locked defaultContact, or whatever's
  // been picked in the contact chip so far). The submit-time pass re-resolves
  // tokens against the FINAL contact, so an apply-before-pick still ends up
  // personalized.
  const handleApplyTemplate = (template: TaskTemplate) => {
    const contactCtx =
      defaultContact ??
      (watchedContact?.id ? ({ id: watchedContact.id, name: watchedContact.name } as ContactJson) : undefined)
    const applied = applyTaskTemplate(template, { contact: contactCtx })
    setValue(`title`, applied.title, { shouldDirty: true })
    if (applied.description) {
      setValue(`description`, applied.description, { shouldDirty: true })
      setShowDescription(true)
    }
    if (applied.dueDate) setValue(`dueDate`, applied.dueDate, { shouldDirty: true })
    if (applied.ownerId) {
      // Show the owner's name in the chip; fall back silently on lookup failure.
      bg(
        resolveOwner({ ownerId: applied.ownerId })
          .then((owner) => {
            if (owner) setValue(`owner`, { id: owner.id, name: owner.name ?? `` }, { shouldDirty: true })
          })
          // bg() only voids the promise, it does not catch, so "fall back
          // silently" needs an actual catch or the rejection goes unhandled.
          .catch((err) => logWarn(`QuickCreateTaskModal: template owner lookup failed`, err)),
      )
    }
  }

  // The save button stays enabled unless we're mid-submit; zod validation is
  // what surfaces the "title is required" / "pick a location" errors via the
  // onInvalid handler, so the user can always click and see a clear error.
  const canSave = !isSaving

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <form className="flex w-screen flex-col md:w-[560px]" onKeyDown={onRootKeyDown} onSubmit={onSubmit}>
      <div className="flex flex-col gap-3 px-6 pb-5 pt-5">
        {/* Title doubles as the modal header — no separate "Add task" bar
            above (caller passes hideTitle: true to showModal). Saves the
            ~80px the Modal header was eating. Close X is inline at the
            top-right of the title row. */}
        <div className="flex items-start gap-3">
          <Input
            {...register(`title`)}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            type="text"
            placeholder="Task title…"
            // The title input acts as the modal heading, so override the
            // shadcn Input defaults (border, shadow, height, body-sm font)
            // to keep the borderless heading look.
            className="h-auto rounded-none border-0 bg-transparent p-0 text-heading-sm text-strong shadow-none outline-none placeholder:text-minimal focus:border-transparent focus:ring-0 focus-visible:ring-0"
            onKeyDown={(e) => {
              if (e.key === `Enter` && !e.shiftKey) {
                e.preventDefault()
                bg(onSubmit())
              }
            }}
          />
          <TaskTemplatePicker
            trigger={
              <button type="button" className="mt-2 shrink-0 text-[11px] text-primary-600 hover:underline">
                Use template
              </button>
            }
            onSelect={handleApplyTemplate}
          />
          <button
            type="button"
            onClick={() => closeModal()}
            aria-label="Close"
            className="-mr-2 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-subtle transition-colors hover:bg-surface-muted hover:text-strong"
          >
            <Xmark className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        {/* Chip row — all metadata as one-click pills. */}
        <div className="-mx-1 flex flex-wrap items-center gap-1.5">
          <Controller
            name="dueDate"
            control={control}
            render={({ field }) => <DueChip value={field.value} onChange={field.onChange} />}
          />
          <Controller
            name="owner"
            control={control}
            render={({ field }) => (
              <OwnerChip
                value={field.value ?? { id: ``, name: `` }}
                onChange={(u) => field.onChange(u)}
                locationId={taskLocationId}
                workspaceId={workspaceId}
              />
            )}
          />
          {isBulk ? (
            <span className={classNames(chipClass(), `cursor-default hover:border-default hover:bg-surface-raised`)}>
              <ProfileCircle className="h-3.5 w-3.5 text-minimal" strokeWidth={2} />
              <span className="leading-none">
                {bulkCount} {bulkCount === 1 ? `contact` : `contacts`}
              </span>
            </span>
          ) : (
            <Controller
              name="contact"
              control={control}
              render={({ field }) => (
                <ContactChip
                  value={field.value}
                  onChange={(c) => field.onChange(c)}
                  workspaceId={workspaceId}
                  disabled={!!defaultContact}
                />
              )}
            />
          )}
          {!isBulk && !watchedContact?.locationId && (
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <LocationChip
                  value={field.value}
                  onChange={(l) => field.onChange(l)}
                  options={workspaceLocations}
                  loading={locationsLoading && !locations.length}
                />
              )}
            />
          )}
          {!showDescription && (
            <button
              type="button"
              onClick={() => setShowDescription(true)}
              className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-body-sm text-subtle transition-colors hover:bg-surface-muted hover:text-strong"
            >
              + Add description
            </button>
          )}
        </div>

        {/* Description — hidden until requested. Plain textarea instead of a
            full WYSIWYG to keep the surface light; rich formatting is rarely
            needed at task-creation time. */}
        {showDescription && (
          <Textarea
            {...register(`description`)}
            placeholder="Add a description…"
            rows={3}
            className="text-body-sm text-strong placeholder:text-minimal"
          />
        )}
      </div>

      {/* Footer with primary action + ⌘↵ hint. */}
      <div className="flex items-center justify-end gap-2 border-t border-default px-6 py-3">
        <span className="mr-auto text-body-xs text-minimal">
          <kbd className="rounded border border-default bg-surface-subtle px-1.5 py-0.5 font-mono text-[10px]">
            {MODIFIER_KEY} Enter
          </kbd>
          {` `}
          to save
        </span>
        <button
          type="button"
          onClick={() => closeModal()}
          className="inline-flex h-9 items-center rounded-md border border-default bg-surface-raised px-4 text-ui-xs text-default hover:bg-surface-subtle"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSave}
          className={classNames(
            `inline-flex h-9 items-center rounded-md px-4 text-ui-xs font-medium transition-colors`,
            canSave ? `bg-blue-600 text-on-color hover:bg-blue-700` : `cursor-not-allowed bg-blue-300 text-on-color`,
          )}
        >
          {isSaving ? `Saving…` : isBulk ? `Create ${bulkCount} ${bulkCount === 1 ? `task` : `tasks`}` : `Save`}
        </button>
      </div>
    </form>
  )
})

// ─── Chips ─────────────────────────────────────────────────────────────────

/** Shared chip styling — `text-trn-700` neutral chip with hover. */
function chipClass(active = false) {
  return classNames(
    `group inline-flex h-7 items-center gap-1.5 rounded-md border border-default bg-surface-raised px-2 text-body-sm text-default shadow-elevation-1 transition-colors hover:border-strong hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-default/30`,
    active && ``,
  )
}

function DueChip({ value, onChange }: { value: Date; onChange: (d: Date) => void }) {
  const [open, setOpen] = useState(false)
  const floating = useFloating({
    open,
    onOpenChange: setOpen,
    placement: `bottom-start`,
    middleware: [offset(6), flip({ padding: 10 })],
    whileElementsMounted: autoUpdate,
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(floating.context),
    useDismiss(floating.context),
  ])

  // Format the chip label as a human-friendly relative term when possible
  // ("Today", "Tomorrow", "Mon Jun 3"), full date for far-out values.
  const label = useMemo(() => {
    const m = moment(value).startOf(`day`)
    const today = moment().startOf(`day`)
    const diff = m.diff(today, `days`)
    if (diff === 0) return `Today`
    if (diff === 1) return `Tomorrow`
    if (diff === -1) return `Yesterday`
    if (diff > 1 && diff < 7) return m.format(`ddd, MMM D`)
    if (diff < -1 && diff > -7) return `${Math.abs(diff)}d ago`
    if (m.year() === today.year()) return m.format(`MMM D`)
    return m.format(`MMM D, YYYY`)
  }, [value])

  // T = today, M = tomorrow, W = end of this week when the chip has focus —
  // saves a calendar-picker round-trip for the 90% case.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (open) return
    if (e.key === `t` || e.key === `T`) {
      e.preventDefault()
      onChange(moment().startOf(`day`).toDate())
    } else if (e.key === `m` || e.key === `M`) {
      e.preventDefault()
      onChange(moment().add(1, `day`).startOf(`day`).toDate())
    } else if (e.key === `w` || e.key === `W`) {
      e.preventDefault()
      onChange(moment().endOf(`week`).startOf(`day`).toDate())
    }
  }

  return (
    <>
      <button
        ref={floating.refs.setReference}
        type="button"
        className={chipClass()}
        onKeyDown={handleKeyDown}
        title="Click or press T (today) / M (tomorrow) / W (end of week)"
        {...getReferenceProps()}
      >
        <Calendar className="h-3.5 w-3.5 text-minimal" strokeWidth={2} />
        <span className="leading-none">{label}</span>
        <NavArrowDown className="h-3 w-3 text-minimal" strokeWidth={2} />
      </button>
      {open && (
        <FloatingPortal>
          <div
            ref={floating.refs.setFloating}
            style={floating.floatingStyles}
            {...getFloatingProps()}
            className="z-50 w-80 rounded-lg border border-default bg-surface-raised p-2 shadow-elevation-2"
          >
            <CalendarPicker
              date={moment(value).format()}
              onDateChange={(d) => {
                onChange(d)
                setOpen(false)
              }}
              limitToMonth={false}
              allowPastDates
              allowFutureDates
            />
          </div>
        </FloatingPortal>
      )}
    </>
  )
}

function OwnerChip({
  value,
  onChange,
  locationId,
  workspaceId,
}: {
  value: UserOpt
  onChange: (u: UserOpt) => void
  locationId?: string
  workspaceId?: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState(``)
  const [options, setOptions] = useState<UserOpt[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    bg(
      assigneesFetcher({ query: search, locationId, workspaceId }).then((res) => {
        if (cancelled) return
        setOptions(res.hits.map(({ id, name, email, kind, disabled }) => ({ id, name, email, kind, disabled })))
        setLoading(false)
      }),
    )
    return () => {
      cancelled = true
    }
  }, [open, search, locationId, workspaceId])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={chipClass()} title="Task owner">
          {value.id.startsWith(ROLE_ASSIGNEE_PREFIX) ? (
            <Group className="h-4 w-4 text-subtle" strokeWidth={1.75} />
          ) : value.name ? (
            <NameIcon fullName={value.name[0] ?? `?`} heightClassName="h-4" widthClassName="w-4" isTiny />
          ) : (
            <User className="h-3.5 w-3.5 text-minimal" strokeWidth={2} />
          )}
          <span className="leading-none">{value.name || `Owner`}</span>
          <NavArrowDown className="h-3 w-3 text-minimal" strokeWidth={2} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[280px] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search roles and people…" value={search} onValueChange={setSearch} />
          <CommandList>
            {loading ? (
              <div className="p-3 text-center text-body-sm text-subtle">Loading…</div>
            ) : options.length === 0 ? (
              <CommandEmpty>No matches</CommandEmpty>
            ) : (
              <>
                {[
                  { heading: `Roles`, kind: `role` },
                  { heading: `People`, kind: `user` },
                ].map(({ heading, kind }) => {
                  const group = options.filter((opt) => (opt.kind ?? `user`) === kind)
                  if (!group.length) return null
                  return (
                    <CommandGroup key={kind} heading={heading}>
                      {group.map((opt) => (
                        <CommandItem
                          key={opt.id}
                          value={opt.id}
                          disabled={opt.disabled}
                          onSelect={() => {
                            onChange(opt)
                            setOpen(false)
                          }}
                        >
                          {kind === `role` ? (
                            <RoleOptionLabel name={opt.name} disabled={opt.disabled} />
                          ) : (
                            <div className="flex min-w-0 items-center gap-2">
                              <NameIcon
                                fullName={(opt.name ?? opt.email ?? `?`)[0]!}
                                heightClassName="h-5"
                                widthClassName="w-5"
                                isTiny
                              />
                              <span className="truncate">{opt.name || opt.email || opt.id}</span>
                            </div>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )
                })}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function ContactChip({
  value,
  onChange,
  workspaceId,
  disabled,
}: {
  value: ContactOpt | undefined
  onChange: (c: ContactOpt | undefined) => void
  workspaceId?: string
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState(``)
  const contactStore = useContactStore()
  // Shared ContactStore instances: the search merges into the records the other views hold.
  const [options, setOptions] = useState<Contact[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    // The cached hits show at once; the server result replaces them when it arrives.
    const onRevalidate = (res: { hits: Contact[] }) => {
      if (!cancelled) setOptions(res.hits)
    }
    bg(
      contactStore.searchByName({ query: search, workspaceId }, { onRevalidate }).then((res) => {
        if (cancelled) return
        setOptions(res.hits)
        setLoading(false)
      }),
    )
    return () => {
      cancelled = true
    }
  }, [contactStore, open, search, workspaceId])

  const triggerClass = classNames(chipClass(), !value && `text-subtle`, disabled && `cursor-not-allowed opacity-60`)

  return (
    <Popover open={disabled ? false : open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={triggerClass}
          disabled={disabled}
          title={disabled ? `Contact is locked for this task` : `Pick a contact`}
        >
          {value?.name ? (
            <NameIcon fullName={value.name[0] ?? `?`} heightClassName="h-4" widthClassName="w-4" isTiny />
          ) : (
            <ProfileCircle className="h-3.5 w-3.5 text-minimal" strokeWidth={2} />
          )}
          <span className="leading-none">{value?.name || `Add contact`}</span>
          {!disabled && <NavArrowDown className="h-3 w-3 text-minimal" strokeWidth={2} />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[300px] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search contacts…" value={search} onValueChange={setSearch} />
          <CommandList>
            {loading ? (
              <div className="p-3 text-center text-body-sm text-subtle">Loading…</div>
            ) : options.length === 0 ? (
              <CommandEmpty>No contacts</CommandEmpty>
            ) : (
              <CommandGroup heading={search ? undefined : `Recent contacts`}>
                {value && (
                  <CommandItem
                    value="__no-contact__"
                    onSelect={() => {
                      onChange(undefined)
                      setOpen(false)
                    }}
                  >
                    <span className="text-subtle">No contact</span>
                  </CommandItem>
                )}
                {options.map((opt) => (
                  <CommandItem
                    key={opt.id}
                    value={opt.id}
                    onSelect={() => {
                      // The form keeps a plain value, not the observable store instance.
                      onChange({ id: opt.id, name: opt.name ?? undefined, locationId: opt.locationId ?? undefined })
                      setOpen(false)
                    }}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <NameIcon fullName={(opt.name ?? `?`)[0]!} heightClassName="h-5" widthClassName="w-5" isTiny />
                      <span className="truncate">{opt.name || opt.id}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function LocationChip({
  value,
  onChange,
  options,
  loading,
}: {
  value: LocationOpt | undefined
  onChange: (l: LocationOpt) => void
  options: readonly LocationOpt[]
  loading: boolean
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState(``)

  const matching = options.filter((opt) => opt.name?.toLowerCase().includes(search.toLowerCase()))

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="white"
          size="xs"
          className={cn(chipClass(), `font-normal`, !value && `text-subtle`)}
          title="Location for a task without a contact"
        >
          <MapPin className="h-3.5 w-3.5 text-minimal" strokeWidth={2} />
          <span className="leading-none">{value?.name || `Add location`}</span>
          <NavArrowDown className="h-3 w-3 text-minimal" strokeWidth={2} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[260px] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search locations…" value={search} onValueChange={setSearch} />
          <CommandList>
            {loading ? (
              <div className="p-3 text-center text-body-sm text-subtle">Loading…</div>
            ) : matching.length === 0 ? (
              <CommandEmpty>No locations</CommandEmpty>
            ) : (
              <CommandGroup>
                {matching.map((opt) => (
                  <CommandItem
                    key={opt.id}
                    value={opt.id}
                    onSelect={() => {
                      onChange(opt)
                      setOpen(false)
                    }}
                  >
                    <span className="truncate">{opt.name || opt.id}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
